/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { useState } from 'react';
import { Box, Popover } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DropdownItem from 'views/Dashboards-B2B/components/DropdownItem';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import * as S from '../../../../../../../../Dashboard.styled';
import { frLabelToKey, labelToKey } from 'utils/formLabelsToKey';

type Props = {
    selectedCase: any;
    handleSelectedCase: (selectedCase: any) => void;
    options: any;
    customStyleForSearchbar?: boolean;
    onboarding?: boolean;
    mobile: boolean;
    completedForms: Record<string, boolean>;
};

const SelectFormPageDropdown = ({
    selectedCase,
    handleSelectedCase,
    options,
    customStyleForSearchbar,
    onboarding,
    mobile,
    completedForms,
}: Props) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    const currentLabelMap = language === 'FR' ? frLabelToKey : labelToKey;
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (
        event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
    ) => {
        // Give MUI a focusable trigger to restore when the popover closes.
        event.currentTarget.focus();
        setAnchorEl(event.currentTarget);
    };

    const handleOptionClick = (e: React.MouseEvent<HTMLElement>, selectedCase: any) => {
        e.stopPropagation();
        handleSelectedCase(selectedCase);
        handleClose();
    };

    return (
        <>
            <S.LeftDropdown
                role="button"
                tabIndex={0}
                aria-expanded={open}
                aria-controls={id}
                mobile={mobile}
                onboarding={onboarding}
                customStyleForSearchbar={customStyleForSearchbar}
                isActive={!!anchorEl}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleClick(event);
                    }
                }}
                onClick={(event) => {
                    handleClick(event);
                }}
            >
                <S.SelectionContainer>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            width: '100%',
                        }}
                    >
                        {selectedCase ? selectedCase : t('selectForm', { lng: language })}
                        {selectedCase && completedForms[currentLabelMap[selectedCase]] && (
                            <CheckCircleIcon sx={{ color: '#22c55e', fontSize: '1.2rem' }} />
                        )}
                    </Box>
                </S.SelectionContainer>

                <S.DropdownIcon />
            </S.LeftDropdown>
            <Popover
                closeAfterTransition
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                marginThreshold={0}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            overflow: 'visible',
                            mt: '8px',
                            minWidth: '7rem',
                            padding: '0',
                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.09)',
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
                <S.DropDownMenu>
                    {options.map((option: any, i: number) => {
                        const isCompleted = completedForms[currentLabelMap[option]];
                        return (
                            <DropdownItem
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                                key={i}
                                onClick={(e) => handleOptionClick(e, option)}
                            >
                                {option}

                                {isCompleted && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                                        <CheckCircleIcon
                                            sx={{ color: '#22c55e', fontSize: '1.3rem' }}
                                        />
                                    </Box>
                                )}
                            </DropdownItem>
                        );
                    })}
                </S.DropDownMenu>
            </Popover>
        </>
    );
};

export default SelectFormPageDropdown;
